import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { FormGroup } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FileUploadComponent } from "@shared/controls/file-upload/file-upload.component";
import { bytesToMegabytes } from "@shared/utils/bytes-to-megabytes";
import { FileLabelComponent } from "@shared/components/commons/file-label/file-label.component";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";

export type AcceptedFileFormat = '.pdf'
  | '.png'
  | '.svg'
  | '.docx'
  | '.xls'
  | '.xlxs'
  | '.txt';

export interface IFileUploadConfig {
  allowedMaxFileSizeMB: number;
  allowedMaxFilesSummarySizeMB: number;
  maxFilesNumber: number;
  acceptedFormatsList: AcceptedFileFormat[];
}

export const commonFileUploadConfig: IFileUploadConfig = {
  allowedMaxFileSizeMB: 2,
  allowedMaxFilesSummarySizeMB: 20,
  maxFilesNumber: 10,
  acceptedFormatsList: ['.pdf'],
};

@Component({
  selector: 'app-annual-upload-files',
  standalone: true,
  imports: [
    CommonModalFormComponent,
    FileUploadComponent,
    FileLabelComponent,
    DisclaimerComponent
  ],
  templateUrl: 'upload-files-modal.component.html',
  styleUrl: 'upload-files-modal.component.scss'
})
export class UploadFilesModalComponent {
  private activeModal = inject(NgbActiveModal);
  private destroyRef = inject(DestroyRef);

  @Input() config: IFileUploadConfig = commonFileUploadConfig;
  @Input() skipPreview = false;
  @Input() multiple = true;
  @Input() infoMessageHtml: string | null = null;
  @Input() getNoteMessage = this.getDefaultUploadNotification.bind(this);

  @Output() _save = new EventEmitter<File[]>();

  form!: FormGroup;
  errorMessage = '';
  uploadedFiles: File[] = [];

  onFilesLoaded(files: File[]): void {
    if (this.uploadedFiles.length + files.length > this.config.maxFilesNumber) {
      this.setErrorMessage(`The maximum number of files to upload is ${ this.config.maxFilesNumber }. Please use the allowed number of files.`);
      return;
    }

    if (this.config.acceptedFormatsList.length) {
      const isAllowedFileExtension = files.some(file => {
        return this.config.acceptedFormatsList.some((fileExtension) => file.name.endsWith(fileExtension));
      });

      if (!isAllowedFileExtension) {
        this.setErrorMessage(`Uploaded file has not allowed file extension. Please use one of allowed file extensions: ${ this.acceptedFormats }`);
        return;
      }
    }

    const filesSizesMb = files.map((file) => bytesToMegabytes(file.size));
    const isNotAllowedFileSize = filesSizesMb.some((fileSize) => fileSize > this.config.allowedMaxFileSizeMB);
    if (isNotAllowedFileSize) {
      this.setErrorMessage(`Uploaded file has not allowed file is too big. Max file size is ${ this.config.allowedMaxFileSizeMB } MB`);
      return;
    }

    const isNotAllowedFilesSummarySize = filesSizesMb.reduce((summaryFilesSize, fileSize) => summaryFilesSize += fileSize, 0) > this.config.allowedMaxFilesSummarySizeMB;
    if (isNotAllowedFilesSummarySize) {
      this.setErrorMessage(`Uploaded files have not allowed to upload. Files size is too big. Max files summary size is ${ this.config.allowedMaxFilesSummarySizeMB } MB`);
      return;
    }

    this.uploadedFiles = [...this.uploadedFiles, ...files];

    this.setErrorMessage();

    if (this.skipPreview) {
      this.confirm();
    }
  }

  setErrorMessage(errorMessage = ''): void {
    this.errorMessage = errorMessage;
  }

  deleteFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  confirm(): void {
    this._save.emit(this.uploadedFiles);
    this.activeModal.close();
  }

  close(): void {
    this.activeModal.close();
  }

  getDefaultUploadNotification(config: IFileUploadConfig = this.config): string {
    return `NOTE: The maximum number of files to upload is ${ config.maxFilesNumber } and should not exceed ${ config.allowedMaxFileSizeMB } MB.`;
  }

  get uploadNotification(): string {
    return this.getNoteMessage(this.config);
  }

  get acceptedFormats(): string {
    return this.config.acceptedFormatsList.join(', ');
  }

  get save(): Observable<File[]> {
    return this._save.asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef));
  }
}
